import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });

  useEffect(() => {
    if (!token) {
      setError('Token de réinitialisation manquant');
      setValidatingToken(false);
      return;
    }

    // Valider le token
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/validate-reset-token?token=${token}`)
      .then(res => res.json())
      .then(data => {
        setTokenValid(data.valid);
        if (!data.valid) {
          setError(data.message || 'Token invalide');
        }
      })
      .catch(() => {
        setError('Erreur de validation du token');
      })
      .finally(() => {
        setValidatingToken(false);
      });
  }, [token]);

  useEffect(() => {
    // Vérifier la force du mot de passe
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    });
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation des mots de passe
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    const allValid = Object.values(passwordStrength).every(v => v);
    if (!allValid) {
      setError('Le mot de passe ne respecte pas tous les critères');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          new_password: password
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de la réinitialisation');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#205F79] mx-auto"></div>
          <p className="mt-4 text-gray-600">Validation du lien...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">
              <p className="font-medium mb-2">Lien invalide ou expiré</p>
              <p>{error}</p>
            </div>
          </div>
          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-[#205F79] hover:text-[#1a4d61]">
              Demander un nouveau lien
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Nouveau mot de passe
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Choisissez un mot de passe sécurisé
          </p>
        </div>

        {success ? (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  ✓ Mot de passe réinitialisé !
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Votre mot de passe a été modifié avec succès.</p>
                  <p className="mt-2">Redirection vers la page de connexion...</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Nouveau mot de passe
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-[#205F79] focus:border-[#205F79] sm:text-sm"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>

              {password && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-xs font-medium text-gray-700 mb-2">Critères du mot de passe :</p>
                  <ul className="text-xs space-y-1">
                    <li className={passwordStrength.length ? 'text-green-600' : 'text-gray-500'}>
                      {passwordStrength.length ? '✓' : '○'} Au moins 8 caractères
                    </li>
                    <li className={passwordStrength.uppercase ? 'text-green-600' : 'text-gray-500'}>
                      {passwordStrength.uppercase ? '✓' : '○'} Une majuscule (A-Z)
                    </li>
                    <li className={passwordStrength.lowercase ? 'text-green-600' : 'text-gray-500'}>
                      {passwordStrength.lowercase ? '✓' : '○'} Une minuscule (a-z)
                    </li>
                    <li className={passwordStrength.number ? 'text-green-600' : 'text-gray-500'}>
                      {passwordStrength.number ? '✓' : '○'} Un chiffre (0-9)
                    </li>
                  </ul>
                </div>
              )}

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmer le mot de passe
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-[#205F79] focus:border-[#205F79] sm:text-sm"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">Les mots de passe ne correspondent pas</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !Object.values(passwordStrength).every(v => v) || password !== confirmPassword}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#205F79] hover:bg-[#1a4d61] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#205F79] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Réinitialisation...' : 'Réinitialiser mon mot de passe'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
